import { useState, useEffect } from 'react';
import { C } from '../constants';
import { loadArchive, clearArchive } from '../archive';
import { AppShell, TopBar, BackBtn } from '../components/Atoms';
import { fmtDay, hospDay } from '../utils';

export default function ArchiveView({ onBack, shiftCode, onClose }) {
  const [records, setRecords]   = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch]     = useState('');

  useEffect(() => {
    loadArchive(shiftCode).then(all => setRecords([...all].reverse()));
  }, [shiftCode]);

  const filtered = records.filter(r => {
    const q = search.toLowerCase();
    if (!q) return true;
    const p = r.patient;
    return p.name?.toLowerCase().includes(q) || p.mrn?.includes(q) || p.diagnosis?.toLowerCase().includes(q) || r.shiftCode?.toLowerCase().includes(q);
  });

  const grouped = filtered.reduce((acc, r) => {
    const day = r.archivedAt?.slice(0, 10) || 'Unknown';
    if (!acc[day]) acc[day] = [];
    acc[day].push(r);
    return acc;
  }, {});

  const handleClear = () => {
    if (!window.confirm('Clear the entire archive? This cannot be undone.')) return;
    if (!window.confirm('Final confirmation — permanently delete all archived patients?')) return;
    clearArchive(shiftCode);
    setRecords([]);
  };

  const close = onBack || onClose;

  return (
    <AppShell>
      <TopBar
        left={<BackBtn onClick={close} />}
        title="📦 Patient Archive"
        right={records.length > 0
          ? <button onClick={handleClear} style={{ background: '#1A0808', color: '#FF6B6B', border: '1px solid #3A1515', borderRadius: 8, padding: '5px 10px', fontSize: 11, cursor: 'pointer' }}>Clear All</button>
          : null
        }
      />

      <div style={{ padding: '8px 14px 4px', flexShrink: 0 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, MRN, diagnosis, shift code…"
          style={{ width: '100%', background: C.bg2, border: `1px solid ${C.bd}`, borderRadius: 10, padding: '8px 12px', color: C.tx, fontSize: 13 }} />
      </div>
      <div style={{ padding: '4px 14px 6px', flexShrink: 0 }}>
        <div style={{ fontSize: 11, color: C.dim2 }}>
          {records.length === 0 ? 'No archived patients yet.' : `${records.length} archived patient${records.length !== 1 ? 's' : ''}`}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '0 14px 40px' }}>
        {records.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: C.dim2 }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>📦</div>
            <div style={{ fontSize: 14, marginBottom: 6 }}>Archive is empty</div>
            <div style={{ fontSize: 12 }}>Patients marked as Discharged are automatically archived at midnight.</div>
          </div>
        )}

        {Object.entries(grouped).map(([day, recs]) => (
          <div key={day} style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#4A9EFF', letterSpacing: '0.08em', marginBottom: 8, textTransform: 'uppercase' }}>
              Archived {fmtDay(day)} · {recs.length} patient{recs.length !== 1 ? 's' : ''}
            </div>
            {recs.map(r => {
              const p = r.patient;
              const isOpen = expanded === r.id;
              return (
                <div key={r.id} style={{ background: C.bg3, border: `1px solid ${C.bd}`, borderLeft: '3px solid #30D158', borderRadius: 10, marginBottom: 8, overflow: 'hidden' }}>
                  {/* Summary row */}
                  <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => setExpanded(isOpen ? null : r.id)}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#E0EEFA' }}>{p.name || 'Unnamed'}</div>
                      <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>
                        MRN {p.mrn} · Rm {p.room} · {p.age}{p.sex}
                        {p.weight ? ` · ${p.weight}${p.weightUnit || 'kg'}` : ''}
                        {p.consultant ? ` · 👤 ${p.consultant}` : ''}
                      </div>
                      <div style={{ fontSize: 12, color: '#6A9BBC', marginTop: 2 }}>{p.diagnosis}</div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <div style={{ cursor: 'pointer', fontSize: 11, color: C.dim }} onClick={() => setExpanded(isOpen ? null : r.id)}>{isOpen ? '▲' : '▼'}</div>
                      </div>
                      <div style={{ fontSize: 9, color: C.dim2 }}>Shift: {r.shiftCode}</div>
                      <div style={{ fontSize: 9, color: C.dim2 }}>{r.archivedAt ? new Date(r.archivedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</div>
                    </div>
                  </div>

                  {/* Expanded full record */}
                  {isOpen && (
                    <div style={{ borderTop: `1px solid ${C.bd}`, background: C.bg2 }}>

                      {/* Demographics block */}
                      <Section label="Patient Details" color="#7DC4FF">
                        <Row2>
                          <AF label="DOB" value={p.dob} />
                          <AF label="Admitted" value={p.admissionDate ? `${p.admissionDate}${hospDay(p.admissionDate) ? ` (D${hospDay(p.admissionDate)})` : ''}` : null} />
                        </Row2>
                        <Row2>
                          <AF label="Team" value={p.team} />
                          <AF label="Service" value={p.code} />
                        </Row2>
                        {p.allergies && <AF label="⚠ Allergies" value={p.allergies} color="#F4A0A0" labelColor="#FF4545" />}
                      </Section>

                      {/* Clinical narrative */}
                      {(p.cc || p.hpi || p.pmh || p.fh || p.psh || p.ros || p.pe) && (
                        <Section label="Clinical History" color="#C8DFF0">
                          {p.cc  && <AF label="Chief Complaint" value={p.cc} />}
                          {p.hpi && <AF label="HPI" value={p.hpi} />}
                          {p.pmh && <AF label="PMH" value={p.pmh} />}
                          {p.fh  && <AF label="Family History" value={p.fh} />}
                          {p.psh && <AF label="Social / Surgical History" value={p.psh} />}
                          {p.ros && <AF label="Review of Systems" value={p.ros} />}
                          {p.pe  && <AF label="Physical Exam" value={p.pe} />}
                        </Section>
                      )}

                      {/* Issues */}
                      {(p.issuesList || []).length > 0 && (
                        <Section label="Active Issues at Discharge" color="#FF9500">
                          {p.issuesList.map(i => (
                            <div key={i.id} style={{ fontSize: 12, color: C.tx, marginBottom: 4 }}>
                              <span style={{ color: '#FF9500', fontWeight: 700 }}>•</span> {i.text}
                              {i.detail && <div style={{ fontSize: 11, color: C.dim, marginLeft: 12, marginTop: 2 }}>{i.detail}</div>}
                            </div>
                          ))}
                          {(p.resolvedIssues || []).length > 0 && (
                            <>
                              <div style={{ fontSize: 9, fontWeight: 800, color: '#30D158', textTransform: 'uppercase', marginTop: 8, marginBottom: 4 }}>Resolved</div>
                              {p.resolvedIssues.map(i => (
                                <div key={i.id} style={{ fontSize: 12, color: '#2A5A3A', marginBottom: 2 }}>✓ {i.text}{i.resolvedDate ? ` (${i.resolvedDate})` : ''}</div>
                              ))}
                            </>
                          )}
                        </Section>
                      )}

                      {/* Procedures */}
                      {(p.procedureList || []).length > 0 && (
                        <Section label="Procedures" color="#BF5AF2">
                          {p.procedureList.map(pr => (
                            <div key={pr.id} style={{ fontSize: 12, color: C.tx, marginBottom: 4 }}>
                              <span style={{ fontWeight: 700, color: '#BF5AF2' }}>{pr.name}</span>
                              <span style={{ color: C.dim }}> — {pr.status}{pr.date ? ` · ${pr.date}` : ''}{pr.physician ? ` · ${pr.physician}` : ''}</span>
                              {pr.note && <div style={{ fontSize: 11, color: C.dim, marginLeft: 12, marginTop: 2 }}>{pr.note}</div>}
                            </div>
                          ))}
                        </Section>
                      )}

                      {/* Medications */}
                      {(p.medsList || []).length > 0 && (
                        <Section label="Medications" color="#30D158">
                          {(p.medsList || []).filter(m => !m.dc).map(m => {
                            const abxDay = m.isAbx && m.startDate ? Math.max(1, Math.floor((Date.now() - new Date(m.startDate)) / 86400000) + 1) : null;
                            return (
                              <div key={m.id} style={{ fontSize: 12, color: C.tx, marginBottom: 3 }}>
                                <span style={{ fontWeight: 700 }}>{m.name}</span> {m.dose} {m.route} {m.freq}
                                {abxDay && <span style={{ color: '#FF9500', fontSize: 11 }}> [ABX D{abxDay}{m.abxTotal ? `/${m.abxTotal}` : ''}]</span>}
                                {m.startDate && <span style={{ color: C.dim, fontSize: 10 }}> · from {m.startDate}</span>}
                              </div>
                            );
                          })}
                          {(p.medsList || []).filter(m => m.dc).length > 0 && (
                            <>
                              <div style={{ fontSize: 9, fontWeight: 800, color: C.dim, textTransform: 'uppercase', marginTop: 6, marginBottom: 3 }}>Discontinued</div>
                              {(p.medsList || []).filter(m => m.dc).map(m => (
                                <div key={m.id} style={{ fontSize: 11, color: '#2A4A3A', marginBottom: 2 }}>✗ {m.name} {m.dose}{m.dcDate ? ` (DC'd ${m.dcDate})` : ''}{m.dcNote ? ` — ${m.dcNote}` : ''}</div>
                              ))}
                            </>
                          )}
                        </Section>
                      )}

                      {/* IV Fluids */}
                      {(p.ivList || []).length > 0 && (
                        <Section label="IV Fluids" color="#64D2FF">
                          {(p.ivList || []).filter(iv => !iv.dc).map(iv => (
                            <div key={iv.id} style={{ fontSize: 12, color: C.tx, marginBottom: 2 }}>
                              {iv.fluid}{iv.rate ? ` @ ${iv.rate}` : ''}{iv.duration ? ` — ${iv.duration}` : ''}
                            </div>
                          ))}
                        </Section>
                      )}

                      {/* Diet */}
                      {(p.dietList || []).filter(d => !d.dc).length > 0 && (
                        <Section label="Diet Orders" color="#FFD60A">
                          {(p.dietList || []).filter(d => !d.dc).map(d => (
                            <div key={d.id} style={{ fontSize: 12, color: C.tx, marginBottom: 2 }}>
                              {d.order}{d.note ? ` — ${d.note}` : ''}
                            </div>
                          ))}
                        </Section>
                      )}

                      {/* Vitals — last 3 entries */}
                      {(p.vitalsLog || []).length > 0 && (
                        <Section label={`Vitals (${p.vitalsLog.length} entries)`} color="#A0C8FF">
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                              <thead>
                                <tr style={{ borderBottom: `1px solid ${C.bd}` }}>
                                  {['Date','Time','BP','HR','RR','Temp','SpO2'].map(h => (
                                    <th key={h} style={{ padding: '3px 6px', textAlign: 'left', color: C.dim, fontWeight: 700, fontSize: 9, textTransform: 'uppercase' }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {[...p.vitalsLog].sort((a,b) => (b.date+b.time).localeCompare(a.date+a.time)).map((v, i) => (
                                  <tr key={i} style={{ borderBottom: `1px solid ${C.bd}22`, background: i % 2 === 0 ? 'transparent' : C.bg3 + '44' }}>
                                    <td style={{ padding: '3px 6px', color: C.dim, fontSize: 10 }}>{v.date}</td>
                                    <td style={{ padding: '3px 6px', color: C.dim, fontSize: 10 }}>{v.time || '—'}</td>
                                    <td style={{ padding: '3px 6px', color: C.tx, fontWeight: 600 }}>{v.vBP || '—'}</td>
                                    <td style={{ padding: '3px 6px', color: C.tx }}>{v.vHR || '—'}</td>
                                    <td style={{ padding: '3px 6px', color: C.tx }}>{v.vRR || '—'}</td>
                                    <td style={{ padding: '3px 6px', color: C.tx }}>{v.vT || '—'}</td>
                                    <td style={{ padding: '3px 6px', color: C.tx }}>{v.vO2 ? `${v.vO2}%` : '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </Section>
                      )}

                      {/* Labs — most recent set */}
                      {(p.labsLog || []).length > 0 && (
                        <Section label={`Labs (${p.labsLog.length} sets)`} color="#FF9500">
                          {[...p.labsLog].sort((a,b) => (b.date+b.time).localeCompare(a.date+a.time)).map((set, si) => (
                            <div key={si} style={{ marginBottom: 10 }}>
                              <div style={{ fontSize: 10, fontWeight: 800, color: '#FF9500', marginBottom: 4 }}>{set.date}{set.time ? ` ${set.time}` : ''}</div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px' }}>
                                {(set.entries || []).filter(e => e.name && e.value).map((e, ei) => (
                                  <span key={ei} style={{ fontSize: 11, color: e.flag === 'H' ? '#FF9500' : e.flag === 'L' ? '#64D2FF' : C.tx }}>
                                    {e.name}: <strong>{e.value}</strong>{e.unit ? ` ${e.unit}` : ''}{e.flag ? ` [${e.flag}]` : ''}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </Section>
                      )}

                      {/* Imaging */}
                      {(p.imagingLog || []).length > 0 && (
                        <Section label="Imaging" color="#64D2FF">
                          {[...p.imagingLog].sort((a,b) => (b.date+b.time).localeCompare(a.date+a.time)).map((img, i) => (
                            <div key={i} style={{ marginBottom: 8 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: '#64D2FF' }}>{img.date} — {img.modality}{img.study ? ` · ${img.study}` : ''}</div>
                              {img.findings && <div style={{ fontSize: 11, color: C.tx, marginTop: 2, lineHeight: 1.5 }}>{img.findings}</div>}
                            </div>
                          ))}
                        </Section>
                      )}

                      {/* Referrals */}
                      {(p.referLog || []).length > 0 && (
                        <Section label="Referrals" color="#FF6B9D">
                          {p.referLog.map((ref, i) => (
                            <div key={i} style={{ fontSize: 12, color: C.tx, marginBottom: 4 }}>
                              <span style={{ fontWeight: 700, color: '#FF6B9D' }}>{ref.service || ref.specialty || '—'}</span>
                              <span style={{ color: C.dim }}> · {ref.status || '—'}{ref.date ? ` · ${ref.date}` : ''}</span>
                              {ref.note && <div style={{ fontSize: 11, color: C.dim, marginLeft: 10, marginTop: 2 }}>{ref.note}</div>}
                            </div>
                          ))}
                        </Section>
                      )}

                      {/* Tasks */}
                      {(p.todos || []).length > 0 && (
                        <Section label="Tasks" color="#FF9500">
                          {p.todos.map(t => (
                            <div key={t.id} style={{ fontSize: 12, color: t.done ? '#2A5A3A' : '#CCAA70', marginBottom: 3, display: 'flex', gap: 8, alignItems: 'center' }}>
                              <span style={{ textDecoration: t.done ? 'line-through' : 'none' }}>{t.done ? '✓' : '⏳'} {t.text}</span>
                              {t.type && t.type !== 'general' && (
                                <span style={{ fontSize: 9, color: t.type === 'labs' ? '#FF9500' : '#64D2FF', background: (t.type === 'labs' ? '#FF9500' : '#64D2FF') + '22', border: `1px solid ${(t.type === 'labs' ? '#FF9500' : '#64D2FF')}44`, borderRadius: 4, padding: '1px 5px' }}>{t.type.toUpperCase()}</span>
                              )}
                            </div>
                          ))}
                        </Section>
                      )}

                      {/* Fluid I/O */}
                      {(p.ioLog || []).length > 0 && (
                        <Section label="Fluid I/O" color="#4A9EFF">
                          {p.ioLog.map((day, di) => {
                            const inMl  = (day.inputs  || []).reduce((s,x) => s + (parseInt(x.amount) || 0), 0);
                            const outMl = (day.outputs || []).reduce((s,x) => s + (parseInt(x.amount) || 0), 0);
                            const bal   = inMl - outMl;
                            return (
                              <div key={di} style={{ marginBottom: 8 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#4A9EFF', marginBottom: 3 }}>
                                  {day.date} — In: {inMl}mL · Out: {outMl}mL · Balance: <span style={{ color: bal > 0 ? '#FF6B6B' : '#64D2FF' }}>{bal > 0 ? '+' : ''}{bal}mL</span>
                                </div>
                              </div>
                            );
                          })}
                        </Section>
                      )}

                      {/* Endorsement note */}
                      {p.note && (
                        <Section label="Endorsement Note" color="#30D158">
                          <div style={{ fontSize: 12, color: '#A0D8A8', lineHeight: 1.6 }}>{p.note}</div>
                        </Section>
                      )}

                      {/* Discharge orders */}
                      {(p.dischargeOrders?.medications || p.dischargeOrders?.followUp || p.dischargeOrders?.others) && (
                        <Section label="Discharge Orders" color="#30D158">
                          {p.dischargeOrders.medications && <AF label="Discharge Medications" value={p.dischargeOrders.medications} />}
                          {p.dischargeOrders.followUp    && <AF label="Follow-up"              value={p.dischargeOrders.followUp}    />}
                          {p.dischargeOrders.others      && <AF label="Other Instructions"      value={p.dischargeOrders.others}      />}
                        </Section>
                      )}

                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </AppShell>
  );
}

function Section({ label, color = C.dim, children }) {
  return (
    <div style={{ padding: '10px 14px', borderTop: `1px solid ${C.bd}` }}>
      <div style={{ fontSize: 9, fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>{label}</div>
      {children}
    </div>
  );
}

function Row2({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>{children}</div>;
}

function AF({ label, value, color, labelColor }) {
  if (!value) return null;
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ fontSize: 9, fontWeight: 800, color: labelColor || C.dim, textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 12, color: color || C.tx, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{value}</div>
    </div>
  );
}
